import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import toast from 'react-hot-toast';

const CHENETHA = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Cg fill='none' stroke='%23c9a84c' stroke-width='0.35' opacity='0.28'%3E%3Cpolygon points='16,3 29,16 16,29 3,16'/%3E%3Cpolygon points='16,8 24,16 16,24 8,16'/%3E%3Ccircle cx='16' cy='16' r='2.5' fill='%23c9a84c' opacity='0.12'/%3E%3Cline x1='16' y1='0' x2='16' y2='32' stroke-width='0.18'/%3E%3Cline x1='0' y1='16' x2='32' y2='16' stroke-width='0.18'/%3E%3C/g%3E%3C/svg%3E")`;

const NAV_LINKS = [
  { label: 'New Arrivals', to: '/collections?is_new=1' },
  { label: 'Silk Sarees',  to: '/collections/silk-sarees',   children: [{ label: 'Kanjivaram Silk', to: '/collections/silk-sarees' }, { label: 'Zari / Jari Work', to: '/collections/jari-sarees' }] },
  { label: 'Cotton',       to: '/collections/cotton-sarees', children: [{ label: 'Cotton Handloom', to: '/collections/cotton-sarees' }, { label: 'Ikat Patterns', to: '/collections/ikat' }] },
  { label: 'Wedding',      to: '/collections/wedding' },
  { label: 'Festival',     to: '/collections/festival' },
  { label: 'All Sarees',   to: '/collections' },
  { label: 'Videos',      to: '/videos' },
  { label: 'Instagram',   to: '/instagram' },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { cart }         = useCart();
  const navigate         = useNavigate();
  const location         = useLocation();
  const [scrolled,     setScrolled]     = useState(false);
  const [drawerOpen,   setDrawerOpen]   = useState(false);
  const [searchOpen,   setSearchOpen]   = useState(false);
  const [searchQ,      setSearchQ]      = useState('');
  const [hoverMenu,    setHoverMenu]    = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const searchRef = useRef(null);
  const isHome = location.pathname === '/';

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn, { passive: true });
    fn();
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => { if (searchOpen) setTimeout(() => searchRef.current?.focus(), 80); }, [searchOpen]);
  useEffect(() => {
    if (drawerOpen) {
      // Lock page scroll when drawer open (save current scroll pos)
      const scrollY = window.scrollY;
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.dataset.scrollY = scrollY;
    } else {
      // Restore page scroll
      document.documentElement.style.overflow = '';
      const scrollY = parseInt(document.documentElement.dataset.scrollY || '0');
      if (scrollY) window.scrollTo(0, scrollY);
    }
    return () => {
      document.documentElement.style.overflow = '';
    };
  }, [drawerOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out');
      navigate('/');
    } catch (e) {
      // logout always succeeds locally even if server fails
      navigate('/');
    } finally {
      setDrawerOpen(false);
      setUserMenuOpen(false);
    }
  };

  const handleSearch = e => {
    e.preventDefault();
    if (searchQ.trim()) { navigate(`/collections?search=${encodeURIComponent(searchQ.trim())}`); setSearchOpen(false); setSearchQ(''); }
  };

  const darkNav = isHome;
  const navBg   = darkNav
    ? scrolled ? { background: `${CHENETHA}, linear-gradient(180deg,#120505,#1a0808)`, boxShadow: '0 4px 32px rgba(0,0,0,0.5)' }
               : { background: `${CHENETHA}, linear-gradient(180deg,rgba(10,2,2,0.92),rgba(18,5,5,0.85))`, backdropFilter: 'blur(8px)' }
    : scrolled ? { background: 'rgba(253,248,240,0.98)', boxShadow: '0 2px 20px rgba(122,31,31,0.08)', backdropFilter: 'blur(12px)' }
               : { background: 'rgba(253,248,240,0.96)', backdropFilter: 'blur(8px)' };

  const logoColor  = darkNav ? '#e8c96a' : '#7a1f1f';
  const linkColor  = darkNav ? 'rgba(255,255,255,0.88)' : '#2d1f1f';
  const iconColor  = darkNav ? 'rgba(255,255,255,0.85)' : '#2d1f1f';
  const borderColor = darkNav ? 'rgba(201,168,76,0.25)' : 'rgba(232,221,208,0.8)';

  const iconBtn = { width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', color: iconColor, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'none' };

  return (
    <>
      {/* Announcement bar */}
      <div style={{ background: darkNav ? '#0a0202' : '#2d1f1f', color: '#e8c96a', textAlign: 'center', fontSize: 'clamp(9px,1.2vw,11px)', letterSpacing: '0.5px', padding: '7px 16px', fontFamily: "'Jost',sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: '0 4px' }}>
        <span>🚚 Free Shipping above ₹999</span>
        <span className="hidden-mobile" style={{ margin: '0 14px', opacity: 0.3 }}>✦</span>
        <span className="hidden-mobile">🏺 GI Certified Handloom Weaves</span>
        <span className="hidden-mobile" style={{ margin: '0 14px', opacity: 0.3 }}>✦</span>
        <span className="hidden-mobile">📞 +91 99636 86116</span>
      </div>

      {/* Main nav */}
      <nav className="main-nav" style={{ position: 'fixed', top: 32, left: 0, right: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 clamp(14px,3vw,36px)', height: 72, borderBottom: `1px solid ${borderColor}`, transition: 'all 0.35s ease', ...navBg }}>
        {darkNav && <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg,transparent,rgba(201,168,76,0.5),rgba(201,168,76,0.8),rgba(201,168,76,0.5),transparent)', pointerEvents: 'none' }} />}

        {/* Hamburger */}
        <button className="hamburger-btn" onClick={() => setDrawerOpen(true)} aria-label="Menu" style={{ display: 'none', flexDirection: 'column', gap: 5, padding: 8, background: 'none', border: 'none', cursor: 'pointer' }}>
          {[0,1,2].map(i => <span key={i} style={{ width: 22, height: 2, background: iconColor, display: 'block', borderRadius: 2 }} />)}
        </button>

        {/* Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{ fontSize: 26 }}>🥻</span>
          <div>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 700, color: logoColor, lineHeight: 1 }}>Venkatagiri</div>
            <div style={{ fontSize: 8, letterSpacing: '4px', textTransform: 'uppercase', color: '#c9a84c', opacity: 0.8 }}>Handlooms</div>
          </div>
        </Link>

        {/* Desktop links */}
        <div className="nav-links-desktop hidden-mobile" style={{ display: 'flex', gap: 2, flex: 1, justifyContent: 'center' }}>
          {NAV_LINKS.map(link => (
            <div key={link.to} style={{ position: 'relative', padding: '4px 0' }}
              onMouseEnter={() => link.children && setHoverMenu(link.label)}
              onMouseLeave={() => setHoverMenu(null)}>
              <Link to={link.to} style={{ fontFamily: "'Jost',sans-serif", fontSize: 'clamp(10px,1vw,12px)', letterSpacing: '1px', textTransform: 'uppercase', color: linkColor, textDecoration: 'none', fontWeight: 500, padding: '8px clamp(8px,1vw,14px)', display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
                {link.label} {link.children && <span style={{ fontSize: 8, opacity: 0.6 }}>▾</span>}
              </Link>
              {link.children && hoverMenu === link.label && (
                <div style={{ position: 'absolute', top: '100%', left: 0, background: darkNav ? '#1a0808' : '#fff', border: darkNav ? '1px solid rgba(201,168,76,0.2)' : '1px solid var(--border)', boxShadow: '0 12px 40px rgba(0,0,0,0.15)', minWidth: 210, padding: '8px 0', zIndex: 200 }}>
                  {link.children.map(c => (
                    <Link key={c.to} to={c.to} style={{ padding: '10px 20px', fontSize: 13, color: darkNav ? 'rgba(255,255,255,0.8)' : '#2d1f1f', textDecoration: 'none', display: 'block', fontFamily: "'Cormorant Garamond',serif" }}
                      onMouseEnter={e => e.currentTarget.style.background = darkNav ? 'rgba(201,168,76,0.1)' : '#fdf8f0'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      {c.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          <button onClick={() => setSearchOpen(true)} style={iconBtn} aria-label="Search">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          </button>

          <div style={{ position: 'relative' }}>
            {user ? (
              <>
                <button onClick={() => setUserMenuOpen(v => !v)} style={iconBtn}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: darkNav ? 'linear-gradient(135deg,#c9a84c,#e8c96a)' : '#7a1f1f', color: darkNav ? '#1a0808' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Playfair Display',serif", fontSize: 13, fontWeight: 700 }}>{user.first_name[0].toUpperCase()}</div>
                </button>
                {userMenuOpen && (
                  <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 150 }} onClick={() => setUserMenuOpen(false)} />
                    <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, background: '#fff', border: '1px solid var(--border)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', minWidth: 220, padding: '10px 0', zIndex: 200 }}>
                      <div style={{ padding: '4px 18px', fontFamily: "'Cormorant Garamond',serif", fontSize: 17, fontWeight: 600 }}>{user.first_name} {user.last_name}</div>
                      <div style={{ padding: '0 18px 6px', fontSize: 11, color: '#aaa' }}>{user.email}</div>
                      <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #f0e8e0' }} />
                      {[{ to: '/account', label: '👤 My Profile' }, { to: '/orders', label: '📦 My Orders' }, { to: '/wishlist', label: '❤️ Wishlist' }].map(l => (
                        <Link key={l.to} to={l.to} onClick={() => setUserMenuOpen(false)} style={{ display: 'block', padding: '9px 18px', fontSize: 13, color: '#2d1f1f' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#fdf8f0'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>{l.label}</Link>
                      ))}
                      <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #f0e8e0' }} />
                      <button onClick={handleLogout} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 18px', fontSize: 13, color: '#c00', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>Logout</button>
                    </div>
                  </>
                )}
              </>
            ) : (
              <Link to="/login" style={iconBtn}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </Link>
            )}
          </div>

          <Link to="/wishlist" style={iconBtn} aria-label="Wishlist">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
          </Link>

          <Link to="/cart" style={{ ...iconBtn, position: 'relative' }} aria-label="Cart">
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
            {cart.count > 0 && <span style={{ position: 'absolute', top: 2, right: 2, background: darkNav ? '#c9a84c' : '#7a1f1f', color: darkNav ? '#1a0808' : '#fff', fontSize: 9, fontWeight: 700, minWidth: 16, height: 16, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>{cart.count}</span>}
          </Link>
        </div>
      </nav>

      {/* Mobile Drawer */}
      {drawerOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex' }} onClick={() => setDrawerOpen(false)}>
          <div style={{ width: 290, maxWidth: '85vw', background: `${CHENETHA}, linear-gradient(180deg,#1a0808,#120505)`, height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid rgba(201,168,76,0.2)' }}>
              <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: '#e8c96a' }}>🥻 Venkatagiri</span>
              <button onClick={() => setDrawerOpen(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 24, cursor: 'pointer' }}>✕</button>
            </div>
            {user && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: '1px solid rgba(201,168,76,0.12)', background: 'rgba(201,168,76,0.05)' }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg,#c9a84c,#e8c96a)', color: '#1a0808', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Playfair Display',serif", fontSize: 18 }}>{user.first_name[0]}</div>
                <div><div style={{ fontWeight: 600, fontSize: 15, color: '#fff' }}>{user.first_name}</div><div style={{ fontSize: 11, color: 'rgba(201,168,76,0.7)' }}>{user.email}</div></div>
              </div>
            )}
            <div style={{ flex: 1, padding: '8px 0' }}>
              {NAV_LINKS.map(link => (
                <div key={link.to}>
                  <Link to={link.to} onClick={() => setDrawerOpen(false)} style={{ display: 'block', fontFamily: "'Jost',sans-serif", fontSize: 13, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.82)', textDecoration: 'none', padding: '13px 20px', borderBottom: '1px solid rgba(201,168,76,0.08)' }}>{link.label}</Link>
                  {link.children && <div style={{ background: 'rgba(0,0,0,0.2)' }}>{link.children.map(c => <Link key={c.to} to={c.to} onClick={() => setDrawerOpen(false)} style={{ display: 'block', fontFamily: "'Cormorant Garamond',serif", fontSize: 15, color: 'rgba(201,168,76,0.75)', textDecoration: 'none', padding: '10px 20px 10px 36px', borderBottom: '1px solid rgba(201,168,76,0.06)' }}>↳ {c.label}</Link>)}</div>}
                </div>
              ))}
              <div style={{ margin: '8px 0', borderTop: '1px solid rgba(201,168,76,0.15)' }} />
              {user ? (
                <>
                  {[{ to: '/account', label: '👤 My Account' }, { to: '/orders', label: '📦 My Orders' }, { to: '/wishlist', label: '❤️ Wishlist' }].map(l => <Link key={l.to} to={l.to} onClick={() => setDrawerOpen(false)} style={{ display: 'block', padding: '12px 20px', fontSize: 13, color: 'rgba(255,255,255,0.75)', textDecoration: 'none', fontFamily: "'Jost',sans-serif" }}>{l.label}</Link>)}
                  <button onClick={handleLogout} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 20px', fontSize: 13, color: '#ff8888', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Jost',sans-serif" }}>Logout</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setDrawerOpen(false)} style={{ display: 'block', padding: '12px 20px', fontSize: 13, color: 'rgba(255,255,255,0.75)', textDecoration: 'none', fontFamily: "'Jost',sans-serif" }}>Login</Link>
                  <Link to="/register" onClick={() => setDrawerOpen(false)} style={{ display: 'block', padding: '12px 20px', fontSize: 13, color: 'rgba(201,168,76,0.85)', textDecoration: 'none', fontFamily: "'Jost',sans-serif" }}>Create Account</Link>
                </>
              )}
            </div>
            <div style={{ padding: '16px 20px', background: 'rgba(0,0,0,0.3)', borderTop: '1px solid rgba(201,168,76,0.15)', fontSize: 11, color: 'rgba(201,168,76,0.6)', fontFamily: "'Jost',sans-serif", lineHeight: 1.8 }}>📞 +91 99636 86116<br/>✉️ care@venkatagirihandlooms.com</div>
          </div>
        </div>
      )}

      {/* Search overlay */}
      {searchOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,2,2,0.88)', zIndex: 3000, display: 'flex', alignItems: 'flex-start', paddingTop: '18vh', justifyContent: 'center', padding: '18vh 16px 0' }} onClick={() => setSearchOpen(false)}>
          <div style={{ width: '100%', maxWidth: 680, background: '#fff' }} onClick={e => e.stopPropagation()}>
            <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input ref={searchRef} value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search sarees, silk, cotton, wedding…" style={{ flex: 1, border: 'none', outline: 'none', fontSize: 17, fontFamily: "'Cormorant Garamond',serif", color: '#2d1f1f', padding: '6px 0' }} />
              {searchQ && <button type="button" onClick={() => setSearchQ('')} style={{ background: 'none', border: 'none', color: '#bbb', fontSize: 18, cursor: 'pointer' }}>✕</button>}
              <button type="submit" style={{ background: '#7a1f1f', color: '#fff', border: 'none', padding: '9px 20px', fontFamily: "'Jost',sans-serif", letterSpacing: '2px', textTransform: 'uppercase', fontSize: 11, cursor: 'pointer' }}>Search</button>
            </form>
            <div style={{ padding: '10px 20px 14px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: '#8a7968', letterSpacing: 1, textTransform: 'uppercase', marginRight: 4 }}>Popular:</span>
              {['Kanjivaram', 'Bridal Silk', 'Ikat Cotton', 'Zari Work', 'Festival'].map(t => (
                <button key={t} onClick={() => { navigate(`/collections?search=${encodeURIComponent(t)}`); setSearchOpen(false); }} style={{ background: 'none', border: '1px solid var(--border)', padding: '5px 12px', fontSize: 12, cursor: 'pointer', color: '#5a3a3a' }}>{t}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
